<template>
  <div
    class="detailed-indicator f-h"
    :class="wrapperClasses"
  >
    <FiltersZone v-if="!asComponent" no-options-button>
      <div slot="shortcuts" class="v-centerer">
        <ChartPeriodSelector
          v-if="displayConfig"
          v-model="displayConfig"
          :preset-id="presetId"
          query-prefix="detailedIndic"
          hide-dates
          show-default-wheel-view
          @presetPick="presetId = $event"
        />
      </div>
    </FiltersZone>
    <!-- Main content, top chart with all the values over a year, bottom chart with 'zoomed' display -->
    <div class="detailed-indicator__main-content f-h-below-filters">
      <!-- Top chart -->
      <div class="detailed-indicator__top">
        <div class="detailed-indicator__top__chart">
          <div class="detailed-indicator__top__chart__wrapper">
            <div class="detailed-indicator__top__canvas-wrapper f-w">
              <canvas ref="topCanvas" class="f-w f-h" />
              <div ref="rangeContainer" class="detailed-indicator__top__range">
                <div ref="rangeInner" class="detailed-indicator__top__range__selector">
                  <div class="detailed-indicator__top__range__selector__handle-w ui-resizable-handle ui-resizable-w" />
                  <div class="detailed-indicator__top__range__selector__handle-e ui-resizable-handle ui-resizable-e" />
                </div>
              </div>
            </div>
          </div>
        </div>
        <div class="detailed-indicator__top__input">
          <div class="detailed-indicator__top__input__wrapper">
            <IndicatorInput
              v-if="!asComponent"
              :indicator="indicator"
              :focused-date="inputFocusedDate"
              :refresh-range="rangeObject"
              min
              shown
              @commentEdit="refresh"
              @clickPrevDate="inputPrevDate"
              @clickNextDate="inputNextDate"
            />
          </div>
        </div>
      </div>
      <!-- End of top chart -->
      <!-- Bottom chart and problems and actions details -->
      <div v-if="!hideBottomSection" class="detailed-indicator__bottom">
        <div class="detailed-indicator__bottom__canvas-wrapper f-w f-h">
          <canvas ref="bottomChart" class="f-w f-h" />
        </div>

        <div v-show="!hidePbActions" class="detailed-indicator__bottom__actions-problems">
          <div class="h-v-centerer">
            <Switcher v-model="actionsProblemsSwitch" :titles="switchItems" />
          </div>
          <div class="detailed-indicator__bottom__actions-problems__period">
            {{ currentPeriod }}
          </div>
          <RouterLink
            class="generic-border generic-border--text-btn generic-border--blue-border detailed-indicator__bottom__add"
            :to="actionsProblemsSwitch === 0
              ? { path: getActionCreationLink(0), query: { newActionIndicators: indicator.id } }
              : { path: getPbReportingLink(0), query: { pbIndicators: indicator.id } }
            "
          >
            <div class="generic-border__left">
              <Icon class="generic-border__left-blue-icon">{{ addExtraBold }}</Icon>
              <span>{{ actionsProblemsSwitch === 0 ? $t('message.ADD_ACTION') : $t('message.ADD_PROBLEM') }}</span>
            </div>
            <Icon class="generic-border__right-arrow">{{ arrowDownLine }}</Icon>
          </RouterLink>
          <div v-if="actionsProblemsSwitch === 0">

            <PlanActionItem
              v-for="action in compActions"
              :key="action.id"
              :action="action"
              :dashboard="true"
            />
          </div>
          <div v-else>
            <ProblemItem v-for="problem in compProblems" :key="problem.id" :problem="problem" />
          </div>
        </div>
      </div>
      <!-- End of bottom chart -->
    </div>
    <!-- End of main content -->

    <!-- Analysis if detailed indicator is displayed from a mgt page -->
    <div v-if="analysis" class="analysis">
      <section>
        <p>{{ $t("message.AVERAGE_GAP_TO_THE_GOAL") }}</p>
        <div class="canvas-wrapper">
          <canvas ref="gapToGoal" class="f-w f-h" />
        </div>
      </section>
      <section />
    </div>
    <!-- End of analysis -->

    <!-- Neo hint message -->
    <div v-if="displayReportHint" class="dark-overlay report-problem-hint h-v-centerer" @click="hideReportHint">
      <div class="message" @click.stop="">
        <img :src="iaHelp" class="ia-head" alt="">
        <!-- eslint-disable-next-line vue/no-v-html -->
        <div class="text" v-html="$t('message.INDIC_NEO_REPORT_PROBLEM')" />

        <RouterLink
          class="generic-border generic-border--text-btn generic-border--blue-border mt10em mb10em"
          :to="{ path: getPbReportingLink(0), query: { pbIndicators: indicator.id } }"
        >
          <div class="generic-border__left">
            <Icon class="generic-border__left-blue-icon">{{ addExtraBold }}</Icon>
            <span>{{ $t('message.ADD_PROBLEM') }}</span>
          </div>
          <Icon class="generic-border__right-arrow">{{ arrowDownLine }}</Icon>
        </RouterLink>
        <Checkbox v-model="reportHintDontShowAgain" :title="$t('message.DONT_SHOW_AGAIN_TEN_DAYS')" />
        <button class="action-btn" @click="hideReportHint">
          {{ $t('message.NO') }}
        </button>
      </div>
    </div>
    <!-- End of Neo hint -->
  </div>
</template>

<script>
/**
 * Page of a specific indicators.
 * A chart at the top displays all the values over a year.
 * Any section of the top chart can be overlayed with a draggable rectangle to focus a specific range.
 * The focused range of values will appear at the bottom of the page as another chart with less values
 * as well as related problems and actions at these dates.
 *
 *
 * @vue-prop {Boolean} [asComponent] - Whether this component is used as a component or a route
 *                                     when used as a component, some options won't be displayed
 * @vue-prop {Boolean} [cumulated] - Whether this indicator is a cumulated library indicator
 * @vue-prop {Boolean} [analysis] - Whether an analysis section should be displayed.
 *                                  Works only with cumulated indicators
 * @vue-prop {Boolean} [hideBottomSection] - Hide the bottom section
 * @vue-prop {Boolean} [hidePbActions] - Hide problems and actions
 * @vue-prop {Boolean} [emitNewRange] - When used as a component, emits the updated range instead of using query params
 * @vue-prop {Array} [workingDays=null] - Team working days can be overridden from props
 * @vue-prop {Object} [boundConfig] - Charts are drawn using specific parameters in a Display Config
 *                                    When DetailedIndicator is used as a component, the config can be defined as props
 * @vue-prop {Number} [focusPeriodIndexes=null] - When != null, focus range is defined by focusPeriodIndexes
 */
import $ from 'jquery';
import { isEqual, omit } from 'lodash';
// Vuex
import { mapState } from 'vuex';
import { FETCH_INDICATOR, FETCH_INDICATOR_DISPLAY_CONFIGS } from '@/store/action-types';
import { GET_INDICATOR_DISPLAY_CONFIGS } from '@/store/mutation-types';
// Components
import ChartPeriodSelector from '@/components/chart-period-selector.vue';
import Checkbox from '@/components/checkbox.vue';
import FiltersZone from '@/components/filters-zone.vue';
import Icon from '@/components/icon.vue';
import IndicatorInput from '@/components/indicator-input.vue';
import PlanActionItem from '@/components/plan-action-item.vue';
import ProblemItem from '@/components/problem-item.vue';
import Switcher from '@/components/switcher.vue';
// Mixins
import mixinCommonColors from '@/mixins/common-colors.vue';
import mixinNeoAssistant from '@/mixins/neo-assistant.vue';
import mixinSocketHandler from '@/mixins/socket-handler.vue';
// Utils
import SocketMessages from '@/utils/classes/SocketMessages';
import IAHelp from '@/utils/classes/IAHelp';
import IAHelpStep from '@/utils/classes/IAHelpStep';
import {
  ChartHelper,
  buildParetoAnalysisChart,
  computeConfigStart,
} from '@/utils/indicator-utils';
import { decodeHTML, calert } from '@/utils/main';
import { LSK_HIDE_INDICATORS_PB_REPORT_HINT } from '@/utils/constants';
import { FETCH_FILTERED_ACTION, FETCH_FILTERED_PROBLEM } from '@/store/modules/problem/action-types';
import { addExtraBold, arrowDownLine } from '@/assets/icons';

const iaHelp = require('@/assets/img/ia-help.png');

export default {

  name: 'DetailedIndicator',

  components: {
    ChartPeriodSelector,
    Checkbox,
    FiltersZone,
    Icon,
    IndicatorInput,
    PlanActionItem,
    ProblemItem,
    Switcher,
  },

  mixins: [mixinCommonColors, mixinNeoAssistant, mixinSocketHandler],

  props: {
    asComponent: Boolean,
    analysis: Boolean,
    hideBottomSection: Boolean,
    hidePbActions: Boolean,
    emitNewRange: Boolean,
    cumulated: Boolean,
    teamIds: {
      type: Array,
      default: undefined,
    },
    workingDays: {
      type: Array,
      default: null,
    },
    boundConfig: {
      type: Object,
      default: null,
    },
    focusPeriodIndexes: {
      type: Object,
      default: null,
    },
  },

  data() {
    return {
      iaHelp,
      addExtraBold,
      arrowDownLine,
      analysisPareto: null,
      // Data
      topChartData: {},
      bottomChartData: {},
      indicator: {},
      demoIndicator: {},
      // Date ranges
      inputFocusedDate: this.$moment(),
      // Charts
      topChart: null,
      bottomChart: null,
      chartHelper: null,
      makeChartTimeout: null,
      focusArrowWidth: 40,
      updateFocusedRangeTimeout: null,
      // Params
      displayValues: false,
      customDisplayData: null,
      displayConfig: null,
      // Actions / Problems
      switchItems: [this.$t('message.ACTIONS'), this.$t('message.PROBLEMS')],
      actions: [],
      problems: [],
      demoActions: [],
      demoProblems: [],
      inputKey: 0,
      destroyed: false,
      displayReportHint: false,
      reportHintAlreadyDisplayed: false,
      reportHintDontShowAgain: false,
      // Properties used by other components. See src/views/readme.md
      iaTeachText: this.$t('sensei.TEACHER_TEXT'),
      iaTeachBtns: [this.teacherBtn('INDICATORS'), this.teacherBtn('RECURRENCE')],
      pageBackButtonAction: () => {
        this.$router.back();
      },
    };
  },

  computed: {
    id() {
      return Number(this.$route.query.detailedIndicatorId);
    },
    /** Returns the current indicator to display */
    compIndicator() {
      return this.demoMode ? this.demoIndicator : this.indicator;
    },
    compActions() {
      return this.demoMode ? this.demoActions : this.actions;
    },
    compProblems() {
      return this.demoMode ? this.demoProblems : this.problems;
    },
    wrapperClasses() {
      return {
        'not-as-page': this.asComponent,
        'no-bottom': this.hideBottomSection,
        'hide-pb-actions': this.hidePbActions,
        'half-left': this.analysis,
      };
    },
    /** Number: ID of the currently selected display configuration */
    presetId: {
      get() {
        const { detailedIndicPresetId } = this.$route.query;
        return detailedIndicPresetId === null || isNaN(detailedIndicPresetId)
          ? (this.defaultPreset && this.defaultPreset.id) || undefined
          : Number(detailedIndicPresetId);
      },
      set(newVal) {
        const cfg = this.allPresets.find(c => c.id === newVal);
        if (
          (cfg.pointUnit === 'd' && ['w', 'M'].includes(this.indicator.inputPeriodUnit))
          || (cfg.pointUnit === 'w' && this.indicator.inputPeriodUnit === 'M')
        ) {
          newVal = (this.defaultPreset && this.defaultPreset.id) || newVal;
        }
        const query = Object.assign({}, { ...this.$route.query, detailedIndicPresetId: newVal });
        if (!isEqual(query, this.$route.query)) {
          this.$router.replace({ query });
        }
      },
    },
    /** Array: All available presets */
    allPresets() {
      return [
        ...this.$store.getters.formIndicDisplayConfigs,
        this.$store.getters.formWheelDisplayConfig,
        ...this.configs.map(item => ({
          ...item,
          pointPeriodObj: { period: item.pointPeriod, unit: item.pointUnit, startDay: item.pointStartDay },
          chartPeriodObj: { period: item.chartPeriod, unit: item.chartUnit },
        })),
      ];
    },
    /** Object: Preset by default */
    defaultPreset() {
      if (this.indicator) {
        return this.allPresets.find(cfg => cfg.pointUnit === this.indicator.inputPeriodUnit);
      }
      return null;
    },
    /** Object: Range of all values, including values not displayed */
    allValuesRange() {
      const dc = computeConfigStart(this.displayConfig, this.topStartDate);
      return {
        start: dc && dc.configStart ? this.$moment(dc.configStart) : this.topStartDate,
        end: this.topEndDate,
      };
    },
    // Dates of the top chart
    topStartDate() {
      return this.$moment().subtract(366, 'd');
    },
    topEndDate() {
      return this.$moment().endOf('day');
    },
    /** Object: Indicator data from state */
    stateIndicator() {
      const key = this.cumulated ? 'cumulatedIndicators' : 'indicators';
      return this.$store.state[key][`${this.indicator.id}`] || this.indicator;
    },
    /** Values to be displayed in charts and editable through indicator-input */
    inputValues() {
      return this.stateIndicator ? this.stateIndicator.inputValues || {} : {};
    },
    /** Values to be displayed in charts and editable through indicator-input */
    objectives() {
      return this.stateIndicator ? this.stateIndicator.objectives || {} : {};
    },
    /** Moment: Date related to the chart point focused by the left bound of the selected period */
    focusedStartDate() {
      return this.allDates[this.allDates.length - this.focusedPeriodIndexes.start]
        || this.$moment(this.topEndDate).subtract(this.focusedPeriodIndexes.start, 'd');
    },
    /** Moment: Date related to the chart point focused by the right bound of the selected period */
    focusedEndDate() {
      return this.allDates[this.allDates.length - this.focusedPeriodIndexes.end] || this.$moment(this.topEndDate);
    },
    focusRangeLeftPosition() {
      if (!this.topChart) {
        return 0;
      }
      const meta = this.topChart.getDatasetMeta(0);
      const xBound = meta.data[this.allDates.length - this.focusedPeriodIndexes.start].x;
      return xBound;
    },
    focusRangeRightPosition() {
      if (!this.topChart) {
        return 0;
      }
      const meta = this.topChart.getDatasetMeta(0);
      const xBound = meta.data[this.allDates.length - this.focusedPeriodIndexes.end].x;
      return xBound;
    },
    /** Object: CSS style applied to the date below the focused range left bound */
    focusStartDateStyle() {
      return { left: `${this.focusRangeLeftPosition}px` };
    },
    /** Object: CSS style applied to the date below the focused range right bound */
    focusEndDateStyle() {
      return { left: `${this.focusRangeRightPosition}px` };
    },
    rangeObject() {
      return {
        start: this.topStartDate,
        end: this.topEndDate.clone().set({ h: 23, m: 59, s: 59 }),
      };
    },
    /** Array: Concatenation of all dates of all points of the top chart */
    allDates() {
      return this.topChartData.points
        ? this.topChartData.points.reduce((dates, p) => dates.concat(p.dates), [])
        : [];
    },
    /** String: Currently zoomed period */
    currentPeriod() {
      const df = this.$t('minDateFormat');
      const start = this.focusedStartDate.format(df);
      const end = this.focusedEndDate.format(df);
      return this.$t('message.OVER_PERIOD_FROM_TO', { start, end });
    },
    // Custom display configurations
    ...mapState({
      configs: state => state[GET_INDICATOR_DISPLAY_CONFIGS.stateKey] || [],
      configsLoading: state => state[GET_INDICATOR_DISPLAY_CONFIGS.loadingKey],
    }),
    // ============
    // Query params
    // ============
    /** Moment: Date of the currently focused date in IndicatorInput overlay */
    inputDate: {
      get() {
        // From query params
        if (this.$route.query.diInputDate) {
          return this.$moment(this.$route.query.diInputDate);
        }
        // Default input date from user-defined settings
        const { indicatorInputDaysGap, workingDays } = this.currentTeam;
        const shiftedDate = this.$moment();
        let nDays = 0;
        while (nDays < indicatorInputDaysGap) {
          if (workingDays[shiftedDate.subtract(1, 'd').isoWeekday() - 1]) {
            nDays++;
          }
        }
        return shiftedDate;
      },
      set(newDate) {
        const query = Object.assign({}, this.$route.query);
        query.diInputDate = newDate.format('YYYY-MM-DD');
        if (!isEqual(query, this.$route.query)) {
          this.$router.replace({ query });
        }
      },
    },
    /** Number: User may choose to display either actions or problems related to the selected period */
    actionsProblemsSwitch: {
      get() {
        return Number(this.$route.query.diActionsProblemsSwitch || 0);
      },
      set(newVal) {
        const query = Object.assign({}, this.$route.query);
        query.diActionsProblemsSwitch = String(newVal);
        if (!isEqual(query, this.$route.query)) {
          this.$router.replace({ query });
        }
      },
    },
    /** Number: Reverse index of the chart point to which the left bound of the focus div is the nearest */
    focusedPeriodIndexes: {
      get() {
        const indexes = this.focusPeriodIndexes;
        if (indexes && indexes.start !== undefined && indexes.end !== undefined) {
          return indexes;
        }
        // If config cumulates point values, there could be less than 15 values
        let leftBound = Number(this.$route.query.difocusLeftBound || 15);
        if (this.topChart) {
          const meta = this.topChart.getDatasetMeta(0);
          leftBound = meta.data.length > leftBound ? leftBound : meta.data.length;
        }
        return {
          start: leftBound,
          end: Number(this.$route.query.difocusRightBound || 1),
        };
      },
      set(newIndexes) {
        if (newIndexes && newIndexes.start !== undefined && newIndexes.end !== undefined) {
          if (this.focusPeriodIndexes !== null) {
            this.$emit('rangeEdit', newIndexes);
            return;
          }
          const query = Object.assign({}, this.$route.query);
          query.difocusLeftBound = `${newIndexes.start}`;
          query.difocusRightBound = `${newIndexes.end}`;
          if (!isEqual(query, this.$route.query)) {
            this.$router.replace({ query });
          }
        }
      },
    },
    // Properties used by other components. See src/views/readme.md
    pageTitle() {
      if (this.compIndicator.name) {
        return decodeHTML(`${this.compIndicator.themeName} - ${this.compIndicator.name}`);
      }
      return this.$t('message.LOADING');
    },
  },

  watch: {
    stateIndicator(newVal, oldVal) {
      if (newVal && newVal.id && (!oldVal || newVal.id !== oldVal.id)) {
        this.displayConfig = this.$store.getters.formIndicDisplayConfigs
          .find(cfg => cfg.pointUnit === newVal.inputPeriodUnit);
      }
    },
    displayValues() {
      this.makeCharts(true);
    },
    inputValues() {
      this.makeCharts(true);
    },
    objectives() {
      this.makeCharts(true);
    },
    /** Triggered when switching between meeting steps */
    id() {
      this.refresh(true);
    },
    // Display config change, refresh
    displayConfig(newVal, oldVal) {
      if (!isEqual(omit(newVal, 'chartEnd'), omit(oldVal, 'chartEnd'))) {
        this.makeCharts(true);
      }
    },
    focusedPeriodIndexes(newVal, oldVal) {
      // Update bottom chart and dates
      if (!isEqual(newVal, oldVal)) {
        this.datesRangeEdited();
      }
    },
    focusRangeLeftPosition(newVal, oldVal) {
      // Handle first mount
      if (oldVal === 0 && newVal !== 0) {
        this.placeRange();
      }
    },
    reportHintDontShowAgain(dontShow) {
      // User ticked the 'hide for the next 10 days' checkbox
      const lsk = `${LSK_HIDE_INDICATORS_PB_REPORT_HINT}${this.id}`;
      if (dontShow) {
        window.localStorage.setItem(lsk, this.$moment().format('YYYY-MM-DD'));
      } else {
        window.localStorage.removeItem(lsk);
      }
    },
    inputDate(newVal) {
      // The objective and values should be refreshed if outside the range bounds
      if (newVal && (newVal.isAfter(this.topEndDate) || newVal.isBefore(this.topStartDate))) {
        this.refresh(true, newVal);
      }
    },
    inputFocusedDate(newVal) {
      // The objective and values should be refreshed if outside the range bounds
      if (newVal && (newVal.isAfter(this.topEndDate) || newVal.isBefore(this.topStartDate))) {
        this.refresh(true, newVal);
      }
    },
    isPbReportingDisplayed() {
      // When creating a new problem from Neo's hint, hide the dark overlay
      if (this.isPbReportingDisplayed && this.displayReportHint) {
        this.displayReportHint = false;
      }
    },
  },

  created() {
    $(window).on(this.resizeEventName, this.datesRangeEdited);

    this.chartHelper = new ChartHelper();
    this.registerSocketHandlers();
    this.refresh();
    // Refresh custom configs
    if (!this.configsLoading) {
      this.$store.dispatch(FETCH_INDICATOR_DISPLAY_CONFIGS);
    }
  },

  beforeDestroy() {
    this.destroyed = true;
    clearTimeout(this.updateFocusedRangeTimeout);
    clearTimeout(this.makeChartTimeout);

    if (this.bottomChart) {
      this.bottomChart.destroy();
    }
    if (this.topChart) {
      this.topChart.destroy();
    }
    if (this.analysisPareto) {
      this.analysisPareto.destroy();
    }
    const inst = $(this.$refs.rangeInner).draggable('instance');
    if (inst) {
      $(this.$refs.rangeInner).draggable('destroy');
      $(this.$refs.rangeInner).resizable('destroy');
    }
  },

  methods: {
    /** Listen to indicator values change events */
    registerSocketHandlers() {
      this.handleSocketMessage(SocketMessages.CC_MSGS.INDICATORS, (msg) => {
        if (Number(msg.id) !== Number(this.indicator.id)) return;
        this.refresh();
      });
      this.handleSocketMessage(SocketMessages.CC_MSGS.PUBLIC_INDICATOR, (msg) => {
        if (Number(msg.id) !== Number(this.indicator.copyPublic)) return;
        this.refresh();
      });
    },
    refresh(useRouteId, inputDate) {
      const indicId = (this.indicator.id && !useRouteId) ? this.indicator.id : this.id;
      if (this.cumulated || !indicId) {
        // Values fetched from parent
        return;
      }
      const { start, end } = this.allValuesRange;
      const startDate = inputDate && inputDate.isBefore(start) ? inputDate : start;
      const endDate = inputDate && inputDate.isAfter(end) ? inputDate : end;
      const params = {
        indicatorId: indicId,
        range: {
          start: startDate.format('YYYY-MM-DD'),
          end: endDate.set({ h: 23, m: 59, s: 59 }).format('YYYY-MM-DD HH:mm:ss'),
        },
      };
      // fetch the problem and the company data (like zones, people names, indicators of the current team, ...)
      this.$store.dispatch(FETCH_INDICATOR, params).then(() => {
        this.indicator = this.$store.state.indicators[`${indicId}`];
        if (!this.indicator) {
          this.indicator = {};
          calert(this.$t('message.INDICATOR_MISSING'));
        }
        this.$nextTick(this.datesRangeEdited);
      });
    },
    /** Initialize the bottom section of the page according to the focus range params */
    datesRangeEdited() {
      // if the dragging is done, fetch related data and update the chart
      clearTimeout(this.updateFocusedRangeTimeout);
      this.updateFocusedRangeTimeout = setTimeout(() => {
        this.makeCharts(true);
        this.$nextTick(this.placeRange);
        this.$nextTick(this.fetchProblemsActions);
      }, 200);
    },
    stateOf(problem) {
      return this.$t('message.PB_STATES')[problem.state - 1];
    },
    /** Two charts in this page. One on top to have a global vue over the last values.
     (Vue may chnage according to the user's preferences)
     And one at the bottom that shows a range of values selected by the user */
    makeCharts(redrawTop) {
      if (!this.indicator || !this.indicator.id) {
        return;
      }

      clearTimeout(this.makeChartTimeout);
      this.makeChartTimeout = setTimeout(() => {
        this.doMakeChart(redrawTop);
      }, 150);
    },
    /** makeCharts allows not to call doMakeCharts repeatedly by using a timeout */
    doMakeChart(redrawTop) {
      if (redrawTop) {
        // Redraw top if necesary
        if (this.topChart != null && this.topChart !== undefined) {
          this.topChart.destroy();
        }
        // When cumulating indicators, fetch indicators of teams filtered by teamIds prop
        const indicators = !this.cumulated || !this.teamIds
          ? undefined
          : (this.stateIndicator.teamIndicators || [])
            .map(id => this.$store.state.indicators[`${id}`])
            .filter(i => i !== undefined && this.teamIds.includes(i.team));
        this.topChartData = this.chartHelper.buildCustomConfigChart(
          this.stateIndicator,
          this.inputValues,
          this.displayConfig,
          this.workingDays || this.currentTeam.workingDays,
          { start: this.topStartDate, end: this.topEndDate },
          indicators,
        );
        this.customDisplayData = this.topChartData;
        const showLabels = this.$refs.topCanvas.offsetWidth > 700;
        this.topChart = this.chartHelper.drawChart(
          this.compIndicator,
          this.topChartData,
          this.$refs.topCanvas,
          this.displayValues,
          false,
          showLabels,
        );
      }
      // Redraw bottom
      if (this.bottomChart != null && this.bottomChart !== undefined) {
        this.bottomChart.destroy();
      }
      if (!this.hideBottomSection) {
        this.bottomChartData = this.customDisplayBottomChart();
        this.bottomChart = this.chartHelper.drawChart(
          this.compIndicator,
          this.bottomChartData,
          this.$refs.bottomChart,
          this.displayValues,
          true,
          true,
        );
      }
      this.updateAnalysis();
    },
    /** Right-side analysis charts (gaps to the goal and distribution)
      Analyze during current period */
    updateAnalysis() {
      if (this.cumulated && this.analysis && this.indicator.indicators) {
        const cRange = { cStart: this.focusedStartDate.clone(), cEnd: this.focusedEndDate.clone() };
        if (this.analysisPareto) {
          this.analysisPareto.destroy();
        }
        this.analysisPareto = buildParetoAnalysisChart(this.$refs.gapToGoal, this.indicator.indicators, cRange);
      }
    },
    /** Init the range selector */
    placeRange() {
      if (this.destroyed) return;
      // Movable overlay above the top chart
      const { rangeContainer, rangeInner } = this.$refs;
      if (!rangeContainer) return;

      // Values in days
      const { focusRangeLeftPosition, focusRangeRightPosition } = this;
      rangeInner.style.left = `${focusRangeLeftPosition}px`;
      rangeInner.style.width = `${focusRangeRightPosition - focusRangeLeftPosition}px`;

      $(rangeInner).resizable({
        handles: {
          e: '.detailed-indicator__top__range__selector__handle-e',
          w: '.detailed-indicator__top__range__selector__handle-w',
        },
        containment: rangeContainer,
        resize: () => {
          this.recalculateDates();
        },
        stop: () => {
          this.recalculateDates();
        },
      }).on('resize', (e) => {
        e.stopPropagation();
      });

      // Edit range
      $(rangeInner).draggable({
        containment: rangeContainer,
        axis: 'x',
        drag: () => {
          this.recalculateDates();
        },
        stop: () => {
          this.recalculateDates();
        },
      });
    },
    /** After editing the selector overlay, this method calculates the dates range and displays the charts */
    recalculateDates() {
      const { rangeInner, topCanvas } = this.$refs;
      const canvasRect = topCanvas.getBoundingClientRect();
      const innerRect = rangeInner.getBoundingClientRect();
      const simulatedLeftEvent = {
        x: innerRect.left - canvasRect.left,
        y: canvasRect.height / 2,
        clientX: innerRect.left,
        clientY: canvasRect.height / 2 + canvasRect.top,
        target: topCanvas,
        srcElement: topCanvas,
      };
      const simulatedRightEvent = {
        x: innerRect.right - canvasRect.left,
        y: canvasRect.height / 2,
        clientX: innerRect.right,
        clientY: canvasRect.height / 2 + canvasRect.top,
        target: topCanvas,
        srcElement: topCanvas,
      };
      const nearestLeftElts = this.topChart && this.topChart
        .getElementsAtEventForMode(simulatedLeftEvent, 'index', { intersect: false }, false);
      const nearestRightElts = this.topChart && this.topChart
        .getElementsAtEventForMode(simulatedRightEvent, 'index', { intersect: false }, false);
      const indexes = { ...this.focusedPeriodIndexes };
      if (nearestLeftElts && nearestLeftElts.length) {
        indexes.start = this.allDates.length - nearestLeftElts[0].index;
      }
      if (nearestRightElts && nearestRightElts.length) {
        indexes.end = this.allDates.length - nearestRightElts[0].index;
      }
      this.focusedPeriodIndexes = indexes;
    },
    // When seeing this component as a page to zoom on an indicator, user may
    // also related problems and actions on the selected period
    fetchProblemsActions() {
      if (this.destroyed || this.indicator.id === undefined || !this.focusedStartDate || !this.focusedEndDate) {
        return;
      }
      this.$store.dispatch(`problem/${FETCH_FILTERED_PROBLEM}`, {
        range: {
          start: this.focusedStartDate.format('YYYY-MM-DD'),
          end: this.focusedEndDate.format('YYYY-MM-DD'),
        },
        indicator: this.indicator.id,
      }).then((d) => {
        this.problems = d;
        this.checkDisplayReportHint();
      });

      this.$store.dispatch(`problem/${FETCH_FILTERED_ACTION}`, {
        range: {
          start: this.focusedStartDate.format('YYYY-MM-DD'),
          end: this.focusedEndDate.format('YYYY-MM-DD'),
        },
        indicator: this.indicator.id,
      }).then((d) => {
        this.actions = d;
      });
    },
    /** Bottom chart data */
    customDisplayBottomChart() {
      const { points, max, min } = this.customDisplayData;
      const bottomData = {
        points: [],
        max,
        min,
      };
      points.forEach((point) => {
        if (point.isBetween(this.focusedStartDate, this.focusedEndDate)) {
          bottomData.points.push(point.clone());
        }
      });
      return bottomData;
    },
    /** Clicked previous in indicator-input */
    inputPrevDate() {
      const date = this.inputFocusedDate.clone();
      // If input period isn't daily, offset to the next period
      if (this.indicator.inputPeriodUnit === 'w') {
        date.startOf('isoWeek');
      } else if (this.indicator.inputPeriodUnit === 'M') {
        date.startOf('month');
      }
      const wd = this.currentTeam.workingDays || [true, true, true, true, true, true, true];
      do {
        date.subtract(1, 'd');
      } while (!wd[date.isoWeekday() - 1]);
      this.inputFocusedDate = date.clone();
    },
    /** Clicked next in indicator-input */
    inputNextDate() {
      const date = this.inputFocusedDate.clone();
      // If input period isn't daily, offset to the next period
      if (this.indicator.inputPeriodUnit === 'w') {
        date.endOf('isoWeek');
      } else if (this.indicator.inputPeriodUnit === 'M') {
        date.endOf('month');
      }
      const wd = this.currentTeam.workingDays || [true, true, true, true, true, true, true];
      do {
        date.add(1, 'd');
      } while (!wd[date.isoWeekday() - 1]);
      this.inputFocusedDate = date.clone();
    },
    /** Display a hint to report a problem if the indicator exceeds the objective */
    checkDisplayReportHint() {
      if (!this.asComponent && !this.reportHintAlreadyDisplayed && this.currentTeam.displayIndicatorHints) {
        // Check if user chose to hide the hint for 10 days
        let hideDate = window.localStorage.getItem(`${LSK_HIDE_INDICATORS_PB_REPORT_HINT}${this.id}`);
        hideDate = this.$moment(hideDate).add(10, 'd');
        if (this.$moment().isBefore(hideDate)) {
          return;
        }

        // If today is focused in the zoomed period
        if (this.focusedEndDate.isSame(this.$moment(), 'd') && this.bottomChartData && this.bottomChartData.points) {
          const { points } = this.bottomChartData;
          const objIsMax = Number(this.indicator.objectiveIsMax) === 1;
          const dataLen = points.length - 1;
          // Check values of 3 last points
          let exceeding = true;
          let nExceeding = 0;
          let nValsChecked = 0;
          let nIterations = 0;
          while (dataLen > nIterations && exceeding && nValsChecked < 3 && nIterations < 10) {
            const { data, objectiveData, pbImpact } = points[dataLen - nIterations];
            if (pbImpact && pbImpact.nReported > 0) {
              // A problem was reported, no prompt
              exceeding = false;
            }
            if (data !== null && data !== undefined && objectiveData !== null && objectiveData !== undefined) {
              nValsChecked++;
              if (data === objectiveData || ((data < objectiveData) === (objIsMax))) {
                exceeding = false;
              } else {
                nExceeding++;
              }
            }
            nIterations++;
          }
          if (exceeding && nExceeding >= 3) {
            // Display message
            this.displayReportHint = true;
            this.reportHintAlreadyDisplayed = true;
          }
        }
      }
    },
    hideReportHint() {
      this.displayReportHint = false;
    },
    createHelpSteps() {
      this.demoIndicator = IAHelp.demoIndicators(this, 250, true);
      this.$nextTick(this.datesRangeEdited);

      this.demoActions = IAHelp.demoActionPlans(this, true);
      this.demoProblems = IAHelp.demoProblems(this);
      return [
        new IAHelpStep(null, this.$t('help.DET_INDIC_INTRO')),
        new IAHelpStep('.detailed-indicator__top__range__selector', this.$t('help.DET_INDIC_RANGE')),
        new IAHelpStep('.detailed-indicator__bottom canvas', this.$t('help.DET_INDIC_BOTTOM')),
        new IAHelpStep('.actions-problems', this.$t('help.DET_INDIC_PB_ACTIONS'), () => {
          this.topChartMenu(true);
        }),
        new IAHelpStep('.menu-wrapper', this.$t('help.DET_INDIC_OPTIONS'), () => {
          this.topChartMenu();
        },
        [
          {
            text: this.$t('help.DET_INDIC_CUSTOM_DISPLAY'),
            action: () => {
              this.neoHelp.hide();
              this.displayConfigEditor = true;
              this.$nextTick(() => {
                this.neoOverlay.help(this.$refs.dispConfig);
              });
            },
          },
        ]),
        // new IAHelpStep(this.$refs.inputBtn, this.$t('help.DET_INDIC_INPUT')),
        new IAHelpStep(null, this.$t('help.DET_INDIC_INPUT_PAGE')),
        new IAHelpStep('.indicator-input:not(.min) .current-value', this.$t('help.DET_INDIC_TYPE')),
        new IAHelpStep('.indicator-input:not(.min) .counter', this.$t('help.DET_INDIC_COUNTER')),
        new IAHelpStep('.indicator-input:not(.min) .save-input', this.$t('help.DET_INDIC_SAVE')),
        new IAHelpStep('.indicator-input:not(.min) .save-input.delete', this.$t('help.DET_INDIC_DELETE')),
        new IAHelpStep('.indicator-input:not(.min) .left-values', this.$t('help.DET_INDIC_LEFT_VAL'), () => {
          $('.left-values').children().eq(1).trigger('click');
        }),
        new IAHelpStep('.indicator-input:not(.min) .right-values', this.$t('help.DET_INDIC_RIGHT_VAL')),
      ];
    },
    helpHidden() {
      this.$nextTick(this.datesRangeEdited);
    },
  },
};
</script>

<style lang="scss">
@import "@/assets/scss/vars.scss";

.detailed-indicator {
  overflow-x: hidden;
  box-sizing: border-box;

  /* Props customizations */
  &.not-as-page {
    &.basic-gray-section {
      margin: 0px;
    }

    .detailed-indicator__top__content {
      height: 100%;
    }

    .content {
      height: 100%;
    }
  }

  &.hide-pb-actions {
    .detailed-indicator__bottom .canvas-wrapper {
      width: 100%;
    }
  }

  &.half-left {
    .detailed-indicator__main-content, .analysis {
      height: 100%;
      width: 50%;
      display: inline-block;
      vertical-align: top;
    }
    .analysis section {
      height: 50%;
      width: 100%;

      p {
        text-align: center;
        height: 1.5em;
      }
    }
    .analysis .canvas-wrapper {
      height: calc(100% - 1.5em);
      width: 100%;
      padding: 10px;
      box-sizing: border-box;
    }
  }

  &__main-content.f-h-below-filters {
    padding: $page-content-padding;
  }

  $top-canvas-padding: 0.6rem;
  $range-container-margin: $top-canvas-padding;
  $right-side-width: 28%;

  &__top {
    height: 45%;
    position: relative;
    box-sizing: border-box;
    display: flex;

    &__canvas-wrapper {
      height: 100%;
      padding: $top-canvas-padding;
      box-sizing: border-box;
      position: relative;
    }

    &__chart {
      height: 100%;
      flex: 1;
      min-width: 0px;
      padding: $page-content-padding;
      box-sizing: border-box;
      background: white;
      border-radius: $main-border-radius;

      &__wrapper {
        height: 100%;
      }
    }

    &__input {
      background: white;
      border-radius: $main-border-radius;
      box-sizing: border-box;
      margin-left: $page-content-padding;
      flex: 0 0 $right-side-width;
      min-width: 0px;
      overflow: auto;
    }

    &__range {
      position: absolute;
      top: $range-container-margin;
      bottom: $range-container-margin;
      left: $range-container-margin;
      right: $range-container-margin;
      pointer-events: none;

      &__selector {
        position: absolute;
        top: 0px;
        bottom: 0px;
        right: 0px;
        background: $color-shizen-blue-transparent;
        border: 1px solid $color-shizen-blue-border;
        border-radius: $high-border-radius;
        pointer-events: all;

        .ui-resizable-handle {
          background: transparent;
        }
      }
    }
  }

  &.no-bottom .detailed-indicator__top {
    height: 100%;
  }

  &.not-as-page {
    .detailed-indicator__top__input {
      display: none;
    }
  }

  &__bottom {
    height: 55%;
    position: relative;
    padding-top: $page-content-padding;
    box-sizing: border-box;
    display: flex;

    &__canvas-wrapper {
      flex: 1;
      min-width: 0px;
      box-sizing: border-box;
      padding: $page-content-padding;
      box-sizing: border-box;
      background: white;
      border-radius: $main-border-radius;
    }

    &__actions-problems {
      flex: 0 0 $right-side-width;
      min-width: 0px;
      margin-left: $page-content-padding;
      padding: $page-content-padding;
      box-sizing: border-box;
      background: white;
      border-radius: $main-border-radius;
      display: flex;
      flex-flow: column;
      height: 100%;
      overflow: auto;

      .plan-action-item {
        width: 100%;
      }

      .problem-item {
        border: 1px solid $color-gray-border;
      }

      &__period {
        font-size: 0.9rem;
        opacity: 0.8;
        margin: 1rem 0;
      }
    }

    &__add {
      flex: 0 0 auto;
      margin-bottom: 0.6rem;
    }
  }

  .report-problem-hint {
    .message {
      background: white;
      border-radius: 20px;
      padding: 2em 2em 1.5em 2em;
      color: #585858;
      position: relative;
      display: flex;
      align-items: center;
      justify-content: center;
      flex-flow: column;

      .text {
        font-weight: 600;
        line-height: 1.6em;
      }
      .ia-head {
        display: block;
        height: 3em;
        width: 3em;
        background: #ffffff;
        border-radius: 2000px;
        box-sizing: border-box;
        position: absolute;
        left: 50%;
        top: 0px;
        transform: translate(-50%, -50%);
      }
      .add-button {
        padding: 0.3em;
        margin-top: 0.8em;
      }
    }

    button {
      margin-top: 0.8em;
    }
  }
}

@media (max-width: $smartphone-landscape) {
  .detailed-indicator {
    &__top, &__bottom {
      flex-flow: column;
      height: auto;

      &__input, &__actions-problems {
        margin-left: 0px;
        margin-top: $page-content-padding;
      }

      &__actions-problems {
        margin-bottom: 10rem;
      }
    }
  }
}
</style>
